/* Sooyun paid-traffic landing page.
   Scoped to [data-lp]; uses its own hooks so it cannot collide with the global sooyun.js.
   Never loads pixel base code — it only emits events when a pixel is already present. */
(function () {
  "use strict";

  const root = document.querySelector("[data-lp]");
  if (!root) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const money = (n) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: root.dataset.currency || "USD"
    }).format(n);

  /* ---------------- UTM capture ----------------
     Held for the session so the values survive any navigation before checkout. */
  const TRACKED_PARAMS = [
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id",
    "fbclid", "ttclid", "gclid"
  ];
  const STORE_KEY = "sooyun_lp_attribution";

  function captureAttribution() {
    const incoming = new URLSearchParams(window.location.search);
    let stored = {};
    try {
      stored = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || "{}");
    } catch (e) {
      stored = {};
    }
    let changed = false;
    TRACKED_PARAMS.forEach((key) => {
      const value = incoming.get(key);
      if (value && stored[key] !== value) {
        stored[key] = value;
        changed = true;
      }
    });
    if (changed) {
      try {
        window.sessionStorage.setItem(STORE_KEY, JSON.stringify(stored));
      } catch (e) {
        /* private mode — fall through, the URL still carries the params */
      }
    }
    return stored;
  }

  const attribution = captureAttribution();

  function checkoutUrl() {
    const base = root.dataset.checkoutUrl || "/checkout";
    const qs = new URLSearchParams();
    Object.keys(attribution).forEach((key) => qs.set(key, attribution[key]));
    const query = qs.toString();
    return query ? base + "?" + query : base;
  }

  /* ---------------- advertising events ----------------
     One event id per event, shared by Meta and TikTok so server-side
     deduplication can match them. Each event name fires at most once. */
  const fired = new Set();
  const metaOn = root.dataset.trackMeta === "true";
  const tiktokOn = root.dataset.trackTiktok === "true";

  function newEventId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }
    return "lp-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
  }

  function track(event, payload, dedupeKey) {
    const key = dedupeKey || event;
    if (fired.has(key)) return;
    fired.add(key);

    const eventId = newEventId();
    if (metaOn && typeof window.fbq === "function") {
      try {
        window.fbq("track", event, payload, { eventID: eventId });
      } catch (e) {
        /* a broken pixel must never break the page */
      }
    }
    if (tiktokOn && window.ttq && typeof window.ttq.track === "function") {
      try {
        window.ttq.track(event, payload, { event_id: eventId });
      } catch (e) {
        /* same */
      }
    }
  }

  /* ---------------- bundle selection ---------------- */
  const inputs = Array.from(root.querySelectorAll('input[name="lp-pack"]'));
  const stickyName = root.querySelector("[data-lp-sticky-name]");
  const stickyPrice = root.querySelector("[data-lp-sticky-price]");
  const errorEl = root.querySelector("#lpBuyError");

  function selected() {
    return inputs.find((input) => input.checked) || inputs[inputs.length - 1];
  }

  function syncSticky() {
    const input = selected();
    if (!input) return;
    if (stickyName) stickyName.textContent = input.dataset.variantTitle || "";
    if (stickyPrice) stickyPrice.textContent = money(parseFloat(input.dataset.variantPrice || "0"));
  }

  inputs.forEach((input) => input.addEventListener("change", syncSticky));
  syncSticky();

  function selectPack(value) {
    const target = inputs.find((input) => input.value === String(value));
    if (!target || target.disabled) return false;
    target.checked = true;
    target.dispatchEvent(new Event("change", { bubbles: true }));
    return true;
  }

  /* ?bundle=3 preselects the three-stick offer and brings the module into view */
  const requested = new URLSearchParams(window.location.search).get("bundle");
  if (requested && selectPack(requested)) {
    const buy = document.querySelector("#lpBuy");
    if (buy) {
      window.requestAnimationFrame(() => {
        /* only scroll when the module is genuinely out of view — on mobile it is
           already in the first viewport, and scrolling would hide the hero */
        const box = buy.getBoundingClientRect();
        const mostlyVisible = box.top < window.innerHeight * 0.8 && box.bottom > 0;
        if (!mostlyVisible) {
          buy.scrollIntoView({
            behavior: reduceMotion.matches ? "auto" : "smooth",
            block: "center"
          });
        }
      });
    }
  }

  /* ---------------- add to cart ---------------- */
  /* Bag link in the header — the way back to Shopify's cart page, which is
     where a shopper can change quantities or remove the line entirely. */
  const bagLink = root.querySelector("[data-lp-bag]");
  const bagCount = root.querySelector("[data-lp-bag-count]");

  function setBagCount(count) {
    if (!bagLink || !bagCount) return;
    bagCount.textContent = String(count);
    if (count > 0) {
      bagLink.removeAttribute("hidden");
    } else {
      bagLink.setAttribute("hidden", "");
    }
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.textContent = message;
    errorEl.hidden = false;
  }

  function clearError() {
    if (!errorEl) return;
    errorEl.hidden = true;
    errorEl.textContent = "";
  }

  async function addToCart(button) {
    const input = selected();
    if (!input) return;
    clearError();

    const price = parseFloat(input.dataset.variantPrice || "0");
    const buttons = Array.from(root.querySelectorAll("[data-lp-add]"));
    buttons.forEach((el) => el.setAttribute("aria-busy", "true"));

    try {
      const chosenId = Number(input.dataset.variantId);
      const res = await fetch(root.dataset.cartAddUrl + ".js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: chosenId, quantity: 1 }] })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || "We could not add that to your bag.");
      }

      /* Each click is intentionally additive. Different pack sizes remain separate
         cart lines, while repeat clicks on the same size increase that line's
         quantity. Shopify's cart page remains the edit/remove surface. */
      let cart = null;
      if (root.dataset.cartUpdateUrl) {
        try {
          const attributed = await fetch(root.dataset.cartUpdateUrl + ".js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ attributes: attribution })
          });
          if (attributed.ok) cart = await attributed.json();
        } catch (e) {
          /* Attribution is optional and must never block a successful add. */
        }
      }
      if (!cart && root.dataset.cartUrl) {
        try {
          const current = await fetch(root.dataset.cartUrl + ".js", {
            headers: { Accept: "application/json" }
          });
          if (current.ok) cart = await current.json();
        } catch (e) {
          /* The cart page will render the authoritative state after navigation. */
        }
      }
      if (cart) setBagCount(cart.item_count);

      track(
        "AddToCart",
        {
          content_type: "product",
          content_ids: [String(input.dataset.variantId)],
          content_name: root.dataset.productTitle,
          value: price,
          currency: root.dataset.currency
        },
        "AddToCart:" + input.dataset.variantId
      );

      track("InitiateCheckout", {
        content_type: "product",
        content_ids: [String(input.dataset.variantId)],
        value: price,
        currency: root.dataset.currency
      });

      window.location.href = checkoutUrl();
    } catch (err) {
      showError(err.message || "Something went wrong. Please try again.");
      if (root.dataset.cartUrl) {
        fetch(root.dataset.cartUrl + ".js", { headers: { Accept: "application/json" } })
          .then((response) => response.ok ? response.json() : null)
          .then((cart) => {
            if (cart) setBagCount(cart.item_count);
          })
          .catch(() => undefined);
      }
      buttons.forEach((el) => el.removeAttribute("aria-busy"));
    }
  }

  root.querySelectorAll("[data-lp-add]").forEach((button) => {
    button.addEventListener("click", () => addToCart(button));
  });

  /* final offer CTA: force the three-stick variant, then add */
  const pickThree = root.querySelector("[data-lp-pick-three]");
  if (pickThree) {
    pickThree.addEventListener("click", () => {
      selectPack(3);
      addToCart(pickThree);
    });
  }

  /* ---------------- sticky bar ---------------- */
  const sticky = root.querySelector("[data-lp-sticky]");
  const buyModule = root.querySelector("#lpBuy");
  if (sticky && buyModule) {
    /* A direct scroll check rather than IntersectionObserver: one rect read on an
       element we already hold, and it stays correct when the observer is throttled. */
    let shown = false;
    const syncSticky3 = () => {
      const past = buyModule.getBoundingClientRect().bottom < 0;
      if (past === shown) return; /* only touch the DOM when the state flips */
      shown = past;
      sticky.classList.toggle("is-shown", past);
      sticky.setAttribute("aria-hidden", past ? "false" : "true");
    };
    window.addEventListener("scroll", syncSticky3, { passive: true });
    window.addEventListener("resize", syncSticky3, { passive: true });
    syncSticky3();
  }

  /* ---------------- hero video ---------------- */
  const hero = root.querySelector("#lpHeroVideo");
  if (hero) {
    if (reduceMotion.matches) {
      hero.removeAttribute("autoplay");
      hero.pause();
    } else {
      const attempt = hero.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => root.classList.add("is-video-blocked"));
      }
    }
  }

  /* ---------------- creator clips ---------------- */
  const clips = Array.from(root.querySelectorAll("[data-lp-clip]"));
  if (clips.length && "IntersectionObserver" in window) {
    const clipObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && !reduceMotion.matches) {
            if (video.preload === "none") video.preload = "metadata";
            video.play().catch(() => undefined);
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.4 }
    );
    clips.forEach((clip) => clipObserver.observe(clip));
  }

  root.querySelectorAll("[data-lp-sound]").forEach((button) => {
    button.addEventListener("click", () => {
      const frame = button.closest(".lp-clip__frame");
      const video = frame && frame.querySelector("[data-lp-clip]");
      if (!video) return;
      const nowMuted = !video.muted;
      /* only one clip audible at a time */
      if (!nowMuted) {
        clips.forEach((other) => {
          if (other !== video) other.muted = true;
        });
        root.querySelectorAll("[data-lp-sound]").forEach((other) => {
          if (other !== button) other.setAttribute("aria-pressed", "false");
        });
      }
      video.muted = nowMuted;
      button.setAttribute("aria-pressed", String(!nowMuted));
      const label = button.querySelector(".lp-sr");
      if (label) label.textContent = nowMuted ? "Unmute this clip" : "Mute this clip";
      if (!nowMuted) video.play().catch(() => undefined);
    });
  });

  /* ---------------- before / after slider ---------------- */
  const stage = root.querySelector("#lpCompare");
  const range = root.querySelector("#lpCompareRange");
  if (stage && range) {
    const apply = (value) => {
      stage.style.setProperty("--lp-split", value + "%");
      range.setAttribute("aria-valuetext", Math.round(value) + "% of the before image shown");
    };
    apply(Number(range.value));
    range.addEventListener("input", () => apply(Number(range.value)));

    /* dragging anywhere on the image, in addition to the (keyboard-operable) range */
    let dragging = false;
    const setFromPointer = (clientX) => {
      const rect = stage.getBoundingClientRect();
      const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
      range.value = String(pct);
      apply(pct);
    };
    stage.addEventListener("pointerdown", (event) => {
      dragging = true;
      stage.setPointerCapture(event.pointerId);
      setFromPointer(event.clientX);
    });
    stage.addEventListener("pointermove", (event) => {
      if (dragging) setFromPointer(event.clientX);
    });
    const stop = () => {
      dragging = false;
    };
    stage.addEventListener("pointerup", stop);
    stage.addEventListener("pointercancel", stop);
  }

  /* ---------------- ViewContent ---------------- */
  const first = selected();
  track("ViewContent", {
    content_type: "product",
    content_ids: first ? [String(first.dataset.variantId)] : [],
    content_name: root.dataset.productTitle,
    value: first ? parseFloat(first.dataset.variantPrice || "0") : undefined,
    currency: root.dataset.currency
  });
})();
